import React, { useEffect, useState } from 'react';
import {
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { IBackupArquivoDetalhes } from '../types';
import { CentralBackupsApi } from '../services/centralBackupsApi';
import { formatarTamanhoBytes, formatarDataHora } from '../utils';
import StatusBadge from '../../ui/StatusBadge';
import moment from 'moment';

interface DetalhesArquivoProps {
  arquivoID: string | null;
  open: boolean;
  onClose: () => void;
  token: string;
}

export const DetalhesArquivo: React.FC<DetalhesArquivoProps> = ({
  arquivoID,
  open,
  onClose,
  token
}) => {
  const theme = useTheme();
  const [carregando, setCarregando] = useState(false);
  const [detalhes, setDetalhes] = useState<IBackupArquivoDetalhes | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (open && arquivoID) {
      setCarregando(true);
      setErro(null);
      setDetalhes(null);
      CentralBackupsApi.obterDetalhesArquivo(arquivoID, token)
        .then((data) => {
          setDetalhes(data);
          setCarregando(false);
        })
        .catch((err) => {
          console.error("Erro ao carregar detalhes do arquivo", err);
          setErro("Não foi possível carregar as informações do arquivo.");
          setCarregando(false);
        });
    }
  }, [open, arquivoID, token]);

  const calcularDuracao = (inicioStr: string, fimStr: string) => {
    const inicio = moment(inicioStr);
    const fim = moment(fimStr);
    const duration = moment.duration(fim.diff(inicio));
    
    const horas = Math.floor(duration.asHours());
    const minutos = duration.minutes();
    const segundos = duration.seconds();

    let result = "";
    if (horas > 0) result += `${horas}h `;
    if (minutos > 0 || horas > 0) result += `${minutos}m `;
    result += `${segundos}s`;
    
    return result;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '16px',
          color: theme.palette.text.primary,
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon color="primary" /> Detalhes do Arquivo
        </Typography>
        <IconButton onClick={onClose} sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: theme.palette.divider, px: 3, py: 2.5 }}>
        {carregando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {erro && (
          <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
            {erro}
          </Typography>
        )}

        {detalhes && !carregando && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Nome do arquivo */}
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                Nome do Arquivo
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary, wordBreak: 'break-all' }}>
                {detalhes.nome}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: theme.palette.divider }} />

            {/* Informações Gerais */}
            <Grid container spacing={2.5}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon sx={{ color: theme.palette.primary.main, fontSize: '1.1rem' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                      Tamanho
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {formatarTamanhoBytes(detalhes.tamanhoBytes)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ color: theme.palette.secondary.main, fontSize: '1.1rem' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                      Data de Cadastro
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {formatarDataHora(detalhes.dataCriacao)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TaskAltIcon sx={{ color: theme.palette.success.main, fontSize: '1.1rem' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                      Situação
                    </Typography>
                    <StatusBadge status={detalhes.status} />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Tarefa Vinculada */}
            {detalhes.tarefa && (
              <>
                <Divider sx={{ borderColor: theme.palette.divider }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontWeight: 700, mb: 1.5 }}>
                    Tarefa Vinculada
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        Nome da Tarefa
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {detalhes.tarefa.nome}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        Tipo
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {detalhes.tarefa.tipo}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {/* Histórico de Execução */}
            {detalhes.historico && (
              <>
                <Divider sx={{ borderColor: theme.palette.divider }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontWeight: 700, mb: 1.5 }}>
                    Histórico da Execução
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        Início da Execução
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {formatarDataHora(detalhes.historico.inicio)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        Fim da Execução
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {formatarDataHora(detalhes.historico.fim)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <SpeedIcon sx={{ color: theme.palette.warning.main, fontSize: '1.1rem' }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                            Duração
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            {calcularDuracao(detalhes.historico.inicio, detalhes.historico.fim)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                          Status do Histórico
                        </Typography>
                        <StatusBadge status={detalhes.historico.status} />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            textTransform: 'none',
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalhesArquivo;
